import type { ApprovalView, InvoiceCheckStatus } from '@salaries/shared';

// PUBLIC approval API — no auth token (the URL token is the credential).

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return (body?.data ?? body) as T;
}

export async function getApproval(token: string): Promise<ApprovalView> {
  return parse<ApprovalView>(await fetch(`/api/approval/${encodeURIComponent(token)}`));
}

/** The amount-check verdict the upload returns, when the box verifies amounts. */
export interface InvoiceUploadResult {
  invoice_uploaded: boolean;
  invoice_check_status: InvoiceCheckStatus | null;
  invoice_amount: number | null;
}

/** Upload a חשבונית file (PDF/PNG/JPG). When the box verifies invoice amounts,
 *  the response carries the verdict — the request takes a few seconds longer
 *  because the document is read before it answers. */
export async function uploadApprovalInvoice(
  token: string,
  file: File,
): Promise<InvoiceUploadResult> {
  return parse<InvoiceUploadResult>(
    await fetch(`/api/approval/${encodeURIComponent(token)}/receipt`, {
      method: 'POST',
      headers: { 'Content-Type': file.type },
      body: file,
    }),
  );
}

/** Attach a חשבונית by URL instead of uploading a file. When the box verifies
 *  amounts the server fetches the document behind the link (or rasterizes the
 *  page) and reads it, so this takes a few seconds and returns the verdict. */
export async function addApprovalInvoiceUrl(
  token: string,
  url: string,
): Promise<{ invoice_url: string } & Omit<InvoiceUploadResult, 'invoice_uploaded'>> {
  return parse(
    await fetch(`/api/approval/${encodeURIComponent(token)}/invoice-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    }),
  );
}

export async function approveSalary(token: string): Promise<void> {
  await parse(
    await fetch(`/api/approval/${encodeURIComponent(token)}/approve`, { method: 'POST' }),
  );
}

/** Ask for corrections with a comment — pauses the link until the owner re-sends. */
export async function requestCorrection(token: string, comment: string): Promise<void> {
  await parse(
    await fetch(`/api/approval/${encodeURIComponent(token)}/request-correction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment }),
    }),
  );
}
